import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import { SiFacebook, SiInstagram, SiX } from 'react-icons/si';
import { motion, AnimatePresence } from 'framer-motion';
import { ROUTE_PATHS } from '@/lib/index';
import { InAppBrowserDetector } from '@/components/InAppBrowserDetector';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId: string) => {
    console.log('Layout scrollToSection called with:', sectionId);
    
    // Zatvori meni prvo
    setMobileMenuOpen(false);
    
    // Sačekaj da se meni zatvori, pa onda skroluj
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      console.log('Element found:', !!element, 'ID:', sectionId);
      
      if (element) {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - headerOffset;
        
        console.log('Scrolling to position:', offsetPosition);
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      } else {
        console.error('Element not found with ID:', sectionId);
        // Pokušaj da skroluješ na vrh ako je hero
        if (sectionId === 'hero') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, 300); // Sačekaj da se animacija menija završi
  };

  const navLinks = [
    { id: 'hero', label: 'Početna', type: 'scroll' },
    { id: 'about', label: 'O Studiju', type: 'scroll' },
    { id: 'services', label: 'Usluge', type: 'scroll' },
    { id: 'process', label: 'Proces', type: 'scroll' },
    { id: 'testimonials', label: 'Iskustva', type: 'scroll' },
    { id: 'blog', label: 'Blog', type: 'scroll' },
    { id: 'horoscopes', label: '🌙 Mesečni & Godišnji Horoskopi', type: 'scroll' },
    { id: 'instagram', label: 'Instagram', type: 'scroll' },
    { id: 'contact', label: 'Kontakt', type: 'scroll' },
  ];

const socialLinks = [
    { icon: SiFacebook, href: 'https://facebook.com', label: 'Facebook' },
    { icon: SiInstagram, href: 'https://www.instagram.com/astroariesstudio/', label: 'Instagram' }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* In-App Browser Detector */}
      <InAppBrowserDetector />
      
      {/* Sticky Forum CTA Bar - NOVO */}
      <div className="fixed top-0 left-0 right-0 z-[102] h-[60px]">
        <div className="relative h-full overflow-hidden">
          {/* Animated gradient background */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 animate-gradient bg-[length:200%_auto]" />
          
          {/* Animated stars */}
          <div className="absolute inset-0 opacity-30">
            {[...Array(15)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-yellow-300 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0, 1.5, 0],
                }}
                transition={{
                  duration: 1.5 + Math.random() * 1.5,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>
          
          {/* Content */}
          <div className="relative h-full flex items-center justify-center px-4">
            <div className="flex items-center gap-3 flex-wrap justify-center">
              {/* Icon */}
              <motion.div
                animate={{
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="text-2xl"
                style={{ filter: "drop-shadow(0 0 10px rgba(251, 191, 36, 0.8))" }}
              >
                🔮
              </motion.div>
              
              {/* Text */}
              <span className="text-white font-bold text-sm sm:text-base">
                <span className="hidden sm:inline">Pitaj Astrologa Daniela — Brzi Odgovori za </span>
                <span className="text-yellow-300">500 RSD</span>
              </span>
              
              {/* CTA Button */}
              <Link to={ROUTE_PATHS.FORUM_CREDITS}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-2 bg-white text-purple-900 font-black text-sm rounded-full shadow-lg hover:shadow-xl transition-all"
                  style={{
                    boxShadow: "0 0 20px rgba(255, 255, 255, 0.5)",
                  }}
                >
                  💬 Pitaj Odmah
                </motion.button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <header className="fixed top-[60px] left-0 right-0 z-[100] transition-all duration-200">
        <div className="bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <button onClick={() => scrollToSection('hero')} className="flex items-center gap-2 group">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary via-primary/80 to-accent flex items-center justify-center shadow-lg">
                  <span className="text-primary-foreground font-bold text-lg">♈</span>
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-foreground group-hover:text-primary transition-colors">Astro Aries</span>
                  <span className="text-xs text-muted-foreground">Studio</span>
                </div>
              </button>

<nav className="hidden md:flex items-center gap-1">
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className={`px-4 py-2 text-sm font-medium transition-all rounded-md ${
                      link.id === 'forum' 
                        ? 'relative text-foreground bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 bg-[length:200%_100%] animate-shimmer hover:from-primary/30 hover:via-accent/30 hover:to-primary/30 shadow-md hover:shadow-lg font-semibold' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {link.label}
                  </button>
                ))}
              </nav>

<div className="hidden md:flex items-center gap-3">
                <Button 
                  size="sm" 
                  className="bg-green-600 hover:bg-green-700 shadow-md gap-2"
                  asChild
                >
                  <a href="https://chat.whatsapp.com/G18FEdGkdDWBcBfjTYJBTx" target="_blank" rel="noopener noreferrer">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Pridruži Se
                  </a>
                </Button>
                <Button onClick={() => scrollToSection('contact')} size="sm" className="shadow-md">
                  Poručite Odmah
                </Button>
              </div>

              {/* Mobilni - Horoskopi dugme + Hamburger */}
              <div className="md:hidden flex items-center gap-2">
                <Button
                  onClick={() => scrollToSection('horoscopes')}
                  size="sm"
                  variant="outline"
                  className="text-xs px-2 py-2 h-auto flex flex-col items-center leading-tight"
                  type="button"
                >
                  <span className="flex items-center gap-1">
                    <span>🌙</span>
                    <span className="font-semibold">Horoskopi</span>
                  </span>
                  <span className="text-[10px] opacity-70">Mesečni & Godišnji</span>
                </Button>
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 text-foreground hover:bg-muted rounded-md transition-colors touch-manipulation active:scale-95"
                  aria-label="Toggle menu"
                  type="button"
                >
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden bg-card border-b border-border relative z-[100]"
              style={{ overflow: 'visible' }}
            >
              <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
                {navLinks.filter(link => link.id !== 'horoscopes').map((link) => (
                  <button
                    key={link.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      scrollToSection(link.id);
                    }}
                    className={`px-4 py-3 text-sm font-medium rounded-md transition-all text-left touch-manipulation ${
                      link.id === 'forum'
                        ? 'text-foreground bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 bg-[length:200%_100%] animate-shimmer hover:from-primary/30 hover:via-accent/30 hover:to-primary/30 shadow-md font-semibold'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted active:text-foreground'
                    }`}
                    type="button"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {link.label}
                  </button>
                ))}
                
                {/* WhatsApp Dugme - Mobilni */}
                <Button 
                  size="sm" 
                  className="mt-2 bg-green-600 hover:bg-green-700 w-full gap-2 touch-manipulation active:scale-95"
                  type="button"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  asChild
                >
                  <a href="https://chat.whatsapp.com/G18FEdGkdDWBcBfjTYJBTx" target="_blank" rel="noopener noreferrer">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Pridruži Se WhatsApp Grupi
                  </a>
                </Button>
                
                <Button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    scrollToSection('contact');
                  }} 
                  className="mt-2 touch-manipulation active:scale-95" 
                  type="button"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Poručite Odmah
                </Button>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="flex-1 pt-[112px]">{children}</main>

      <footer className="bg-card border-t border-border mt-24">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary via-primary/80 to-accent flex items-center justify-center shadow-lg">
                  <span className="text-primary-foreground font-bold text-lg">♈</span>
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-foreground">Astro Aries</span>
                  <span className="text-xs text-muted-foreground">Studio</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Profesionalne astrološke konsultacije koje vam pomažu da razumete svoj životni put i donesete bolje odluke.
              </p>
              <div className="flex gap-3">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all duration-200 hover:scale-110"
                      aria-label={social.label}
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-4">Brzi Linkovi</h3>
<nav className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors text-left"
                  >
                    {link.label}
                  </button>
                ))}
              </nav>
            </div>

<div>
              <h3 className="font-semibold text-foreground mb-4">Kontakt</h3>
              <div className="flex flex-col gap-3 text-sm text-muted-foreground">
                <div>
                  <p className="font-medium text-foreground mb-1">Email</p>
                  <a href="mailto:astroariesstudio@gmail.com" className="hover:text-foreground transition-colors">
                    astroariesstudio@gmail.com
                  </a>
                </div>
                <div>
                  <p className="font-medium text-foreground mb-1">WhatsApp</p>
                  <a href="https://wa.me/381653471080" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                    +381 60 529 3755
                  </a>
                </div>
                <div>
                  <p className="font-medium text-foreground mb-1">WhatsApp Grupa</p>
                  <a href="https://chat.whatsapp.com/G18FEdGkdDWBcBfjTYJBTx" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors text-xs">
                    Pridružite se za dnevne savete
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border mt-12 pt-8 text-center text-sm text-muted-foreground">
            <p>© 2026 Astro Aries Studio. Sva prava zadržana.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}